import type {
  AgencyInsuranceResponse,
  PayorSearchResult,
  PayorType,
} from '../../services/clinicsApi';
import { isInvoiceBilled } from '../../constants/insuranceBilling';

export type InsuranceDedupFields = {
  name: string;
  edi_payer_id?: string | null;
};

export type DuplicateInsurancePreview = {
  totalSourceCount: number;
  createdCount: number;
  skippedCount: number;
};

// Medicare payer types (mirrors backend MEDICARE_PAYOR_TYPES in
// app/constants/payor_classification.py). PDGM is NOT gated on this set — the
// flag can be enabled on any payer type; callers use this only to default the
// toggle On when a Medicare type is picked.
export const MEDICARE_PAYOR_TYPES: PayorType[] = [
  'MEDICARE_TRADITIONAL',
  'MEDICARE_ADVANTAGE',
  'MEDICARE_HMO',
];

export const isMedicarePayorType = (pt: PayorType | null): boolean =>
  pt != null && MEDICARE_PAYOR_TYPES.includes(pt);

/** Matches backend `_insurance_dedup_key` in agency_insurance.py. */
export const insuranceDedupKey = (insurance: InsuranceDedupFields): string => {
  const ediPayerId = (insurance.edi_payer_id ?? '').trim();
  const normalizedName = (insurance.name ?? '').trim().toLowerCase();
  if (ediPayerId) {
    return `payer:${ediPayerId}:${normalizedName}`;
  }
  return `name::${normalizedName}`;
};

export const previewDuplicateInsurances = (
  insurances: Array<
    InsuranceDedupFields & { agency_clinic_id?: string | null }
  >,
  sourceClinicId: string,
  destinationClinicId: string
): DuplicateInsurancePreview => {
  const sourceInsurances = insurances.filter(
    insurance => insurance.agency_clinic_id === sourceClinicId
  );
  const existingKeys = new Set(
    insurances
      .filter(insurance => insurance.agency_clinic_id === destinationClinicId)
      .map(insuranceDedupKey)
  );

  let createdCount = 0;
  let skippedCount = 0;
  for (const insurance of sourceInsurances) {
    const key = insuranceDedupKey(insurance);
    if (existingKeys.has(key)) {
      skippedCount += 1;
    } else {
      existingKeys.add(key);
      createdCount += 1;
    }
  }

  return {
    totalSourceCount: sourceInsurances.length,
    createdCount,
    skippedCount,
  };
};

export const formatDuplicateInsurancePreview = (
  preview: DuplicateInsurancePreview
): string => {
  const { totalSourceCount, createdCount, skippedCount } = preview;

  if (totalSourceCount === 0) {
    return 'The source clinic has no insurances to copy.';
  }

  if (createdCount === 0) {
    return `No insurances will be copied — all ${totalSourceCount} source insurance${
      totalSourceCount === 1 ? '' : 's'
    } already exist on the destination (matching payer ID and name).`;
  }

  const createdLabel = `${createdCount} insurance${createdCount === 1 ? '' : 's'}`;
  if (skippedCount === 0) {
    return `Will copy ${createdLabel}.`;
  }

  return `Will copy ${createdLabel} (${skippedCount} skipped — matching payer ID and name already on the destination).`;
};

/**
 * Whether the WellSky clinic-info sync still owns this insurance. A row keeps
 * its `clinic_key` after the agency leaves WellSky, so the key alone only says
 * where the row came from. The agency's `is_kinnser_enabled` is the switch:
 * no sync writes to an agency with it off.
 */
export function isWellSkySyncedInsurance(
  insurance: Pick<AgencyInsuranceResponse, 'clinic_key'>,
  agencyKinnserEnabled: boolean | null | undefined
): boolean {
  return !!insurance.clinic_key && agencyKinnserEnabled === true;
}

/**
 * What to tell the user after saving an insurance's Payer ID. The save looks
 * the payer up, and Create Claim refuses an insurance it did not find, so a
 * save that leaves the insurance unlinked is a warning, not a success.
 */
export function payerLinkSaveNotice({
  savedPayerId,
  saved,
  wasLinked,
  successTitle,
}: {
  savedPayerId: string;
  saved: Pick<AgencyInsuranceResponse, 'payor_id'> &
    Partial<Pick<AgencyInsuranceResponse, 'bill_type'>>;
  wasLinked: boolean;
  successTitle: string;
}): {
  variant: 'default' | 'destructive';
  title: string;
  description?: string;
  duration?: number;
} {
  if (saved.payor_id) {
    return {
      variant: 'default',
      title: successTitle,
      description: wasLinked ? undefined : "Linked to Roger's payer list.",
    };
  }
  // An invoice claim needs no payer, so an unlinked invoice insurance is fine.
  if (isInvoiceBilled(saved.bill_type) || (!savedPayerId && !wasLinked)) {
    return { variant: 'default', title: successTitle };
  }
  const reason = savedPayerId
    ? `Payer ID ${savedPayerId} isn't in Roger's payer list`
    : 'It has no Payer ID';
  return {
    variant: 'destructive',
    // Longer than the default: it says what to do next.
    duration: 10000,
    title: "Insurance saved, but its payer isn't linked",
    description: `${reason}, so claims can't be created for it. Edit it and pick the payer from the lookup.`,
  };
}

/** The Payer ID last saved on the insurance, and whether it found a payer. */
export type SavedPayerLink = { ediPayerId: string; linked: boolean };

export type PayerLinkTone = 'linked' | 'unlinked' | 'pending';

/**
 * Whether the Payer ID on the form links the insurance to Roger's payer list.
 * Create Claim refuses an insurance with no link, and the save does the
 * matching, so a value typed by hand can only be confirmed by saving.
 */
export const describePayerLink = ({
  payerId,
  pickedPayor,
  savedPayerLink,
  canPick,
  billedByInvoice = false,
}: {
  payerId: string;
  pickedPayor: PayorSearchResult | null;
  savedPayerLink: SavedPayerLink | null;
  canPick: boolean;
  billedByInvoice?: boolean;
}): { tone: PayerLinkTone; text: string } => {
  const pickHint = canPick ? ' Pick the payer from the lookup above.' : '';
  const id = payerId.trim();
  const linked =
    (pickedPayor && id && id === pickedPayor.electronic_payor_id) ||
    (id && savedPayerLink?.linked && id === savedPayerLink.ediPayerId);
  if (billedByInvoice && !linked) {
    return {
      tone: 'pending',
      text: 'Billed by invoice, so claims can be created without a payer.',
    };
  }
  if (pickedPayor && id && id === pickedPayor.electronic_payor_id) {
    return {
      tone: 'linked',
      text: `Links to ${pickedPayor.payor_name} when you save.`,
    };
  }
  if (id && savedPayerLink?.linked && id === savedPayerLink.ediPayerId) {
    return { tone: 'linked', text: "Linked to Roger's payer list." };
  }
  if (!id) {
    return savedPayerLink
      ? {
          tone: 'unlinked',
          text: `Not linked to a payer, so claims can't be created.${pickHint}`,
        }
      : { tone: 'pending', text: `No payer linked yet.${pickHint}` };
  }
  if (savedPayerLink && id === savedPayerLink.ediPayerId) {
    return {
      tone: 'unlinked',
      text: `Payer ID ${id} isn't in Roger's payer list, so claims can't be created.${pickHint}`,
    };
  }
  return {
    tone: 'pending',
    text: `Payer ID ${id} is checked against Roger's payer list when you save.`,
  };
};
